import React, { useState, useEffect } from 'react';
import CalendarSidebar from './CalendarSidebar';
import CalendarGrid from './CalendarGrid';
import EventDrawer from './EventDrawer';
import { useAuth } from '../../context/AuthContext';
import API from '../../api/axiosConfig';

export default function CalendarPage() {
    const { userData } = useAuth();
    const userRole = userData?.role || 'STUDENT'; 

    const [currentDate, setCurrentDate] = useState(new Date());
    // На мобилках по умолчанию включаем "Расписание", на ПК - "Месяц"
    const [currentView, setCurrentView] = useState(window.innerWidth < 768 ? 'schedule' : 'month'); 
    const [miniCalendarDate, setMiniCalendarDate] = useState(new Date());

    // Состояния интерфейса
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768); 
    const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
    const [pickerYear, setPickerYear] = useState(currentDate.getFullYear());

    const [myGroups, setMyGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [filters, setFilters] = useState({
        tests: true, deadlines: true, meetings: true, consultation: true, personal: true, group: true
    });
    
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isEventDrawerOpen, setIsEventDrawerOpen] = useState(false);
    const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);

    // Следим за шириной экрана для переключения режимов
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768 && !isSidebarOpen) setIsSidebarOpen(true);
            if (window.innerWidth < 768 && isSidebarOpen) setIsSidebarOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isSidebarOpen]);

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const endpoint = (userRole === 'TEACHER' || userRole === 'ADMIN') ? '/api/teacher/groups/' : '/api/student/my-groups/';
                const res = await API.get(endpoint);
                setMyGroups(res.data);
                setSelectedGroups(res.data.map(g => g.name));
            } catch (error) { console.error("Ошибка загрузки групп", error); }
        };
        fetchGroups();
    }, [userRole]);

    const handleNavigate = (direction) => {
        const newDate = new Date(currentDate);
        const dir = direction === 'next' ? 1 : -1;
        if (currentView === 'day') newDate.setDate(newDate.getDate() + dir);
        else if (currentView === 'week') newDate.setDate(newDate.getDate() + (7 * dir));
        else if (currentView === 'month' || currentView === 'schedule') newDate.setMonth(newDate.getMonth() + dir);
        else if (currentView === 'year') newDate.setFullYear(newDate.getFullYear() + dir);
        
        setCurrentDate(newDate);
        setMiniCalendarDate(new Date(newDate));
    };

    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setMiniCalendarDate(today);
    };

    const handleMonthSelect = (monthIndex) => {
        const newDate = new Date(currentDate);
        newDate.setFullYear(pickerYear);
        newDate.setMonth(monthIndex);
        setCurrentDate(newDate);
        setMiniCalendarDate(newDate);
        setIsMonthPickerOpen(false);
    };

    const monthNames = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
    const viewOptions = { day: 'День', week: 'Неделя', month: 'Месяц', year: 'Год', schedule: 'Расписание' };

    return (
        <div className="flex flex-col h-screen bg-white w-full font-sans overflow-hidden text-gray-800">
            
            {/* ШАПКА */}
            <header className="flex items-center justify-between px-3 md:px-4 h-14 md:h-16 border-b border-gray-200 shrink-0 bg-white z-20">
                <div className="flex items-center gap-2 md:gap-4">
                    <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
                    </button>
                    
                    <button onClick={handleToday} className="hidden md:block px-4 py-1.5 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition-colors ml-2 mr-2">
                        Сегодня
                    </button>

                    <div className="flex items-center gap-1">
                        <button onClick={() => handleNavigate('prev')} className="p-1 md:p-1.5 rounded-full hover:bg-gray-100 text-gray-600"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg></button>
                        <button onClick={() => handleNavigate('next')} className="p-1 md:p-1.5 rounded-full hover:bg-gray-100 text-gray-600"><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg></button>
                    </div>

                    <button onClick={() => { setPickerYear(currentDate.getFullYear()); setIsMonthPickerOpen(!isMonthPickerOpen); }} className="text-lg md:text-[22px] font-normal text-gray-800 flex items-center gap-1 hover:bg-gray-50 px-2 py-1 rounded-lg transition-colors capitalize">
                        {currentView === 'year' ? `${currentDate.getFullYear()} год` : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`}
                        <svg className={`w-4 h-4 transition-transform ${isMonthPickerOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                </div>

                <div className="relative">
                    <button onClick={() => setIsViewDropdownOpen(!isViewDropdownOpen)} className="flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1.5 border border-gray-300 rounded text-sm font-medium hover:bg-gray-50 transition-colors">
                        <span className="hidden md:inline">{viewOptions[currentView]}</span>
                        <span className="md:hidden">{viewOptions[currentView].substring(0, 3)}.</span>
                        <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {isViewDropdownOpen && (
                        <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 shadow-lg rounded-md py-1 z-50">
                            {Object.entries(viewOptions).map(([key, label]) => (
                                <button key={key} onClick={() => { setCurrentView(key); setIsViewDropdownOpen(false); }} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${currentView === key ? 'bg-blue-50/50 text-blue-600 font-bold' : 'text-gray-700'}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </header>

            {/* ВЫПАДАЮЩЕЕ ОКНО ВЫБОРА МЕСЯЦА/ГОДА */}
            {isMonthPickerOpen && (
                <div className="absolute top-14 left-0 md:left-48 w-full md:w-80 bg-white shadow-xl border-b md:border border-gray-200 z-40 p-4 md:rounded-b-2xl animate-slide-in">
                    <div className="flex justify-between items-center mb-4">
                        <button onClick={() => setPickerYear(y => y - 1)} className="p-2 hover:bg-gray-100 rounded-full">&lt;</button>
                        <span className="font-bold text-lg">{pickerYear}</span>
                        <button onClick={() => setPickerYear(y => y + 1)} className="p-2 hover:bg-gray-100 rounded-full">&gt;</button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        {["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"].map((month, index) => {
                            const isSelected = index === currentDate.getMonth() && pickerYear === currentDate.getFullYear();
                            return (
                                <button key={month} onClick={() => handleMonthSelect(index)} className={`py-3 text-sm font-semibold rounded-full transition-all ${isSelected ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}>
                                    {month}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
            {isMonthPickerOpen && <div className="absolute inset-0 top-14 bg-black/10 z-30" onClick={() => setIsMonthPickerOpen(false)}></div>}

            <div className="flex flex-1 overflow-hidden relative">
                
                {/* ДЕСКТОПНЫЙ СТАТИЧНЫЙ САЙДБАР */}
                <div className={`hidden md:block shrink-0 border-r border-gray-200 overflow-y-auto transition-all duration-300 ease-in-out bg-white ${isSidebarOpen ? 'w-64' : 'w-0 border-r-0'}`}>
                    <CalendarSidebar 
                        miniCalendarDate={miniCalendarDate} setMiniCalendarDate={setMiniCalendarDate} setCurrentDate={setCurrentDate}
                        filters={filters} onFilterChange={(type) => setFilters(prev => ({ ...prev, [type]: !prev[type] }))}
                        myGroups={myGroups} selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups}
                    />
                </div>

                {/* МОБИЛЬНЫЙ ВЫЕЗЖАЮЩИЙ САЙДБАР */}
                {isSidebarOpen && (
                    <div className="md:hidden fixed inset-0 z-50 flex">
                        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIsSidebarOpen(false)}></div>
                        <div className="relative w-4/5 max-w-[300px] bg-white h-full shadow-2xl animate-slide-in overflow-y-auto">
                            <CalendarSidebar 
                                isMobile onClose={() => setIsSidebarOpen(false)}
                                miniCalendarDate={miniCalendarDate} setMiniCalendarDate={setMiniCalendarDate} setCurrentDate={setCurrentDate}
                                filters={filters} onFilterChange={(type) => setFilters(prev => ({ ...prev, [type]: !prev[type] }))}
                                myGroups={myGroups} selectedGroups={selectedGroups} setSelectedGroups={setSelectedGroups}
                            />
                        </div>
                    </div>
                )}

                {/* ОСНОВНАЯ СЕТКА */}
                <div className="flex-1 overflow-hidden bg-white relative flex flex-col min-w-0">
                    <CalendarGrid 
                        userRole={userRole} filters={filters} selectedClasses={selectedGroups}
                        onEventClick={(e) => { setSelectedEvent(e); setIsEventDrawerOpen(true); }}
                        currentDate={currentDate} currentView={currentView} miniCalendarDate={miniCalendarDate}
                    />
                </div>
            </div>

            <EventDrawer isOpen={isEventDrawerOpen} onClose={() => setIsEventDrawerOpen(false)} event={selectedEvent} userRole={userRole} />
        </div>
    );
}